import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { VehicleCard } from "../components/VehicleCard";
import { Seo } from "../components/Seo";
import { Button } from "../components/ui/button";
import { MapPin, ArrowRight, ShieldCheck, HandCoins, Sparkles, CheckCircle2 } from "lucide-react";

export const CITIES = {
  ovar: {
    slug: "carros-usados-ovar",
    city: "Ovar",
    title: "Carros Usados em Ovar | STAND LF — Stand de Automóveis",
    description:
      "Stand de carros usados e seminovos em Ovar (São João de Ovar). Viaturas revistas, com garantia, financiamento e retoma. Veja o nosso stock atual.",
    h1: "Carros usados em Ovar",
    lead:
      "A STAND LF é o seu stand de automóveis em São João de Ovar. Encontre carros usados e seminovos revistos, com garantia e a preços transparentes — sem surpresas.",
    paragraphs: [
      "Se procura carros usados em Ovar, o nosso stand reúne viaturas de várias marcas (Volkswagen, BMW, Mercedes, Renault, Peugeot, Audi, entre outras), todas inspecionadas antes da entrega. Estamos na Rua Cimo de Vila, 1536, a poucos minutos do centro de Ovar.",
      "Tratamos de tudo por si: financiamento à medida com aprovação rápida, retoma do seu automóvel usado e apoio em todo o processo, do primeiro contacto à entrega das chaves.",
    ],
  },
  aveiro: {
    slug: "carros-usados-aveiro",
    city: "Aveiro",
    title: "Carros Usados em Aveiro | STAND LF — Automóveis",
    description:
      "Procura carros usados em Aveiro? A STAND LF, em Ovar, tem viaturas usadas e seminovas revistas, com garantia, financiamento e retoma. A poucos minutos de Aveiro.",
    h1: "Carros usados em Aveiro",
    lead:
      "A STAND LF serve toda a região de Aveiro. Estamos em São João de Ovar, a poucos minutos, com uma seleção de carros usados e seminovos revistos e com garantia.",
    paragraphs: [
      "Compre o seu carro usado perto de Aveiro com total confiança. Todas as viaturas são inspecionadas e entregues prontas a estrear, de várias marcas e segmentos — citadinos, familiares, SUV e comerciais.",
      "Oferecemos financiamento com aprovação rápida e aceitamos a retoma do seu automóvel. Venha conhecer-nos na Rua Cimo de Vila, 1536, São João de Ovar.",
    ],
  },
  porto: {
    slug: "automoveis-usados-porto",
    city: "Porto",
    title: "Automóveis Usados perto do Porto | STAND LF",
    description:
      "Automóveis usados e seminovos ao serviço da região do Porto. STAND LF em São João de Ovar: viaturas revistas, com garantia, financiamento e retoma.",
    h1: "Automóveis usados perto do Porto",
    lead:
      "A STAND LF serve a região do Porto a partir de São João de Ovar. Uma seleção de automóveis usados e seminovos revistos, com garantia e preços transparentes.",
    paragraphs: [
      "Se procura automóveis usados na zona do Porto, vale a pena a curta deslocação até ao nosso stand. Trabalhamos com várias marcas e segmentos, sempre com viaturas inspecionadas e prontas a estrear.",
      "Contamos com financiamento à medida, retoma do seu carro e acompanhamento em todo o processo. Estamos na Rua Cimo de Vila, 1536, São João de Ovar.",
    ],
  },
};

const BENEFITS = [
  { icon: ShieldCheck, title: "Garantia incluída", text: "Viaturas com garantia e histórico." },
  { icon: Sparkles, title: "Revisão completa", text: "Inspeção multiponto antes da entrega." },
  { icon: HandCoins, title: "Financiamento", text: "Soluções à medida com aprovação rápida." },
];

export default function LocalLanding({ cityKey }) {
  const cfg = CITIES[cityKey];
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const canonical = `https://standlf.com/${cfg.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    name: "STAND LF",
    description: cfg.description,
    url: canonical,
    telephone: "+351924020938",
    areaServed: cfg.city,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Rua Cimo de Vila, 1536",
      postalCode: "3880-743",
      addressLocality: "São João de Ovar",
      addressRegion: "Aveiro",
      addressCountry: "PT",
    },
  };

  return (
    <div className="App">
      <Seo title={cfg.title} description={cfg.description} canonical={canonical} jsonLd={jsonLd} />
      <Navbar />

      <section className="relative pt-[128px] pb-16 bg-zinc-950 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <span className="inline-flex items-center gap-1.5 text-primary font-bold text-xs tracking-[0.25em] uppercase">
            <MapPin className="w-4 h-4" /> {cfg.city}
          </span>
          <h1 data-testid="local-h1" className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 max-w-4xl">
            {cfg.h1} <span className="text-primary">com garantia</span>
          </h1>
          <p className="text-zinc-300 text-base sm:text-lg mt-5 max-w-2xl leading-relaxed">{cfg.lead}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button data-testid="local-cta-stock" onClick={() => navigate("/")} className="rounded-full h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-transform hover:-translate-y-0.5">
              Ver viaturas em stock <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a href="https://wa.me/351924020938" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-full h-12 px-6 font-semibold border-2 border-white/20 bg-transparent text-white hover:bg-white hover:text-zinc-950">
                Falar connosco
              </Button>
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid sm:grid-cols-3 gap-4">
          {BENEFITS.map((b) => (
            <div key={b.title} className="bg-secondary/60 rounded-2xl p-6 border border-black/5">
              <b.icon className="w-6 h-6 text-primary mb-3" />
              <h3 className="font-heading font-bold text-lg">{b.title}</h3>
              <p className="text-muted-foreground text-sm mt-1">{b.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 max-w-3xl space-y-4">
          {cfg.paragraphs.map((p) => (
            <p key={p.slice(0, 30)} className="text-muted-foreground leading-relaxed">{p}</p>
          ))}
          <ul className="grid sm:grid-cols-2 gap-2 pt-2">
            {["Viaturas revistas e com garantia", "Financiamento com aprovação rápida", "Retoma do seu automóvel", "Apoio do início à entrega das chaves"].map((t) => (
              <li key={t} className="flex items-center gap-2 text-sm text-foreground/80">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>

        {vehicles.length > 0 && (
          <div className="mt-16">
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Viaturas em destaque</h2>
              <Link to="/" data-testid="local-see-all" className="text-primary font-semibold text-sm hover:underline flex items-center gap-1">
                Ver todas <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.map((v, i) => <VehicleCard key={v.id} vehicle={v} index={i} />)}
            </div>
          </div>
        )}

        <div className="mt-16 bg-primary/5 border border-primary/15 rounded-3xl p-8 sm:p-10 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Visite o stand em São João de Ovar</h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Rua Cimo de Vila, 1536, 3880-743 São João de Ovar — a poucos minutos de {cfg.city}.</p>
          <Button onClick={() => navigate("/")} className="mt-6 rounded-full h-12 px-7 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
            Ver todas as viaturas
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
}
